/* eslint-disable */
import Oidc, { User } from 'oidc-client';
import { alarm, profile } from '@/store';
import {
    createOidcAuth,
    LogLevel,
    OidcAuth,
    SignInType,
} from 'vue-oidc-client/vue2';
import { reactive } from 'vue';
import addSeconds from 'date-fns/addSeconds';
const loco = window.location;
const appRootUrl = `${loco.protocol}//${loco.host}${process.env.BASE_URL}`;
import { configureMessageHub } from '@/plugins/messageHub';
import { Log } from '@/plugins/Logging';
let authObj = null as OidcAuth | null;

const sessionExpiryGraceSeconds = 30;
let sessionExpiryIntervalId: number | null = null;
let sessionSignOutStarted = false;

export const sessionExpiryPrompt = reactive({
    isOpen: false,
    secondsRemaining: sessionExpiryGraceSeconds,
    renewalFailed: false,
    isSigningIn: false,
    errorMessage: "",
});

function clearSessionExpiryInterval() {
    if (sessionExpiryIntervalId) {
        window.clearInterval(sessionExpiryIntervalId);
        sessionExpiryIntervalId = null;
    }
}

function closeSessionExpiryPrompt() {
    clearSessionExpiryInterval();
    sessionExpiryPrompt.isOpen = false;
    sessionExpiryPrompt.secondsRemaining = sessionExpiryGraceSeconds;
    sessionExpiryPrompt.renewalFailed = false;
    sessionExpiryPrompt.isSigningIn = false;
    sessionExpiryPrompt.errorMessage = "";
}

function signOutAfterSessionExpiry() {
    if (sessionSignOutStarted) return;

    sessionSignOutStarted = true;
    clearSessionExpiryInterval();
    sessionExpiryPrompt.isOpen = false;
    alarm.clearAlarmData();

    if (!authObj) {
        window.location.href = appRootUrl;
        return;
    }

    authObj.stopSilentRenew();
    authObj.signOut().then(() => {
        window.location.href = appRootUrl;
    }).catch((error) => {
        console.error("OIDC sign-out failed after session expiry:", error);
        window.location.href = appRootUrl;
    });
}

function startSessionExpiryCountdown() {
    clearSessionExpiryInterval();
    const deadline = Date.now() + sessionExpiryPrompt.secondsRemaining * 1000;

    sessionExpiryIntervalId = window.setInterval(() => {
        const secondsRemaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
        sessionExpiryPrompt.secondsRemaining = secondsRemaining;

        if (secondsRemaining === 0) {
            signOutAfterSessionExpiry();
        }
    }, 250);
}

function showSessionExpiryPrompt(renewalFailed: boolean) {
    if (sessionSignOutStarted) return;

    if (sessionExpiryPrompt.isOpen) {
        if (renewalFailed) sessionExpiryPrompt.renewalFailed = true;
        return;
    }

    sessionSignOutStarted = false;
    sessionExpiryPrompt.isOpen = true;
    sessionExpiryPrompt.secondsRemaining = sessionExpiryGraceSeconds;
    sessionExpiryPrompt.renewalFailed = renewalFailed;
    sessionExpiryPrompt.isSigningIn = false;
    sessionExpiryPrompt.errorMessage = "";
    startSessionExpiryCountdown();
}

export async function staySignedIn() {
    if (!authObj || sessionExpiryPrompt.isSigningIn) return;

    clearSessionExpiryInterval();
    sessionExpiryPrompt.isSigningIn = true;
    sessionExpiryPrompt.errorMessage = "";

    try {
        const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        await authObj.signIn({ state: { to: returnTo } });
        sessionExpiryPrompt.isSigningIn = false;
        if (sessionExpiryPrompt.isOpen) startSessionExpiryCountdown();
    } catch (error) {
        sessionExpiryPrompt.isSigningIn = false;
        sessionExpiryPrompt.errorMessage = "Sign-in could not be started. Please try again before the timer expires.";
        console.error("OIDC sign-in could not be started from the session prompt:", error);
        if (sessionExpiryPrompt.isOpen) startSessionExpiryCountdown();
    }
}

export function logOutFromSessionPrompt() {
    signOutAfterSessionExpiry();
}

export async function configureOidc() {
    if (authObj) return Promise.resolve(authObj);

    const configFetch = await fetch('/_configuration/MarineOneApp');
    const config = await configFetch.json();

    authObj = createOidcAuth(
        'main',
        SignInType.Window,
        appRootUrl,
        {
            ...config,
            // test use
            prompt: 'login',
            accessTokenExpiringNotificationTime: sessionExpiryGraceSeconds + 10,
            automaticSilentRenew: false,
            monitorSession: false,

            // max_age: 120,
            silent_redirect_uri: `${appRootUrl}/silent-refresh.html`,
            log_console_warning_active: true,
            log_console_debug_active: false,
        },
        console,
        LogLevel.Debug
    );

    // handle events
    authObj.events.addAccessTokenExpiring(function () {
        showSessionExpiryPrompt(false);
    });
    authObj.events.addAccessTokenExpired(function () {
        alarm.clearAlarmData();
        showSessionExpiryPrompt(false);
    });

    authObj.events.addSilentRenewError(function (err: Error) {
        alarm.clearAlarmData();
        console.warn("OIDC silent renewal failed; prompting before sign-out:", err);
        showSessionExpiryPrompt(true);
    });

    authObj.events.addUserLoaded(function (user: User) {
        closeSessionExpiryPrompt();
        sessionSignOutStarted = false;
        profile.Get().then(() => {
            configureMessageHub().then(() => {
                Log.information('Messagehub started');
            });
        });
    });

    authObj.events.addUserUnloaded(function () {
        closeSessionExpiryPrompt();
        alarm.clearAlarmData();
        window.location.href = appRootUrl;
    });

    authObj.events.addUserSignedOut(function () {
        closeSessionExpiryPrompt();
        window.location.href = appRootUrl;
    });

    authObj.events.addUserSessionChanged(function () {
        alarm.clearAlarmData();
    });
    const getExpirationTime = (expires_At: number): Date => {
        var jan1970 = new Date('1970-01-01T00:00:00Z');
        return addSeconds(jan1970, expires_At);
    };
    return authObj;
}